// Receipt approval queue.
//
// Surfaces every receipt awaiting review (or already archived) so any
// staff member can approve, reject, or archive. The list excludes
// approved + sent rows — those flow into the standard /receipts page.
//
// Locked decisions:
//   - Approve = flip status to approved only (no auto-send).
//   - Archive = terminal state via POST /receipts/:enc_id/archive.
//   - Approved receipts cannot be archived (server returns 409).
//
// Self-approval guard:
//   The server already enforces "you cannot approve a receipt you
//   created" (403). The UI suppresses the buttons up-front by comparing
//   `created_by_name` to the current user's name. Archive is still
//   allowed (matches server, which has no creator restriction on archive).

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Archive,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  XCircle,
} from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { useReceiptList } from './hooks/useReceiptList';
import { useArchiveReceipt } from './hooks/useReceiptMutations';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useToast } from '../../hooks/useToast';
import { useAuthStore } from '../../store/authStore';
import type { ReceiptStatus, ReceiptSummary } from '../../types/api';
import styles from './ReceiptApprovalPage.module.css';

const STATUS_MAP: Record<ReceiptStatus, BadgeVariant> = {
  pending_review: 'warning',
  approved: 'success',
  rejected: 'danger',
  archived: 'muted',
};

const STATUS_LABELS: Record<ReceiptStatus, string> = {
  pending_review: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  archived: 'Archived',
};

const REJECT_MIN_LENGTH = 10;

// Pull a large page so the KPI tiles reflect the full pending set in one
// fetch — there is no aggregate endpoint and the queue should rarely top
// 200 outstanding items per tenant. The Pagination component still works
// because the server returns totalPages.
const KPI_PAGE_LIMIT = 200;

const SELF_APPROVE_TOOLTIP = 'You cannot approve your own receipt.';

type QueueStatus = 'pending_review' | 'archived';

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function ageInDays(iso: string): number {
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  const diff = now - then;
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

export function ReceiptApprovalPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const userName = useAuthStore((s) => s.user?.name ?? null);

  const status: QueueStatus =
    params.get('status') === 'archived' ? 'archived' : 'pending_review';
  const dateFrom = params.get('date_from') ?? '';
  const dateTo = params.get('date_to') ?? '';
  const search = params.get('q') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  function setParam(key: string, value: string | null): void {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const list = useReceiptList({
    page,
    limit: KPI_PAGE_LIMIT,
    status,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    search: search || undefined,
  });

  const archiveMutation = useArchiveReceipt();

  const [approveTarget, setApproveTarget] = useState<ReceiptSummary | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ReceiptSummary | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [archiveTarget, setArchiveTarget] = useState<ReceiptSummary | null>(null);
  const [pendingActionEncId, setPendingActionEncId] = useState<string | null>(null);

  const receipts = list.data?.receipts ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const kpis = useMemo(() => {
    const pending = receipts.filter((r) => r.status === 'pending_review');
    const totalValue = pending.reduce((sum, r) => sum + r.amount, 0);
    let oldestDays = 0;
    for (const r of pending) {
      const age = ageInDays(r.created_at);
      if (age > oldestDays) oldestDays = age;
    }
    const todayIso = new Date().toISOString().slice(0, 10);
    const approvedToday = receipts.filter(
      (r) =>
        r.approved_at !== null && r.approved_at.slice(0, 10) === todayIso,
    ).length;
    return {
      pendingCount: pending.length,
      oldestDays,
      totalValue,
      approvedToday,
    };
  }, [receipts]);

  function isOwnReceipt(r: ReceiptSummary): boolean {
    if (userName === null || r.created_by_name === null) return false;
    return r.created_by_name.trim().toLowerCase() === userName.trim().toLowerCase();
  }

  async function handleApproveConfirmed(r: ReceiptSummary): Promise<void> {
    setPendingActionEncId(r.enc_id);
    try {
      await apiClient.post(endpoints.receipts.approve(r.enc_id));
      toast.success('Receipt approved');
      setApproveTarget(null);
      void list.refetch();
    } catch {
      toast.error('Failed to approve receipt.');
    } finally {
      setPendingActionEncId(null);
    }
  }

  async function handleRejectConfirmed(
    r: ReceiptSummary,
    reason: string,
  ): Promise<void> {
    if (reason.trim().length < REJECT_MIN_LENGTH) {
      toast.error(`A rejection reason of at least ${REJECT_MIN_LENGTH} characters is required.`);
      return;
    }
    setPendingActionEncId(r.enc_id);
    try {
      await apiClient.post(endpoints.receipts.reject(r.enc_id), { reason });
      toast.success('Receipt rejected');
      setRejectTarget(null);
      setRejectReason('');
      void list.refetch();
    } catch {
      toast.error('Failed to reject receipt.');
    } finally {
      setPendingActionEncId(null);
    }
  }

  function handleArchiveConfirmed(r: ReceiptSummary): void {
    setPendingActionEncId(r.enc_id);
    archiveMutation.mutate(r.enc_id, {
      onSuccess: () => {
        setArchiveTarget(null);
        void list.refetch();
      },
      onSettled: () => {
        setPendingActionEncId(null);
      },
    });
  }

  const columns: Column<ReceiptSummary>[] = useMemo(
    () => [
      {
        key: 'reference',
        header: 'Reference',
        render: (_v, row) => (
          <button
            type="button"
            className={styles.refLink}
            onClick={() => navigate(`/collections/receipts/${encodeURIComponent(row.enc_id)}`)}
          >
            {row.reference ?? row.unique_id}
          </button>
        ),
      },
      {
        key: 'customer_name',
        header: 'Customer',
        render: (_v, row) => (
          <div>
            <span>{row.customer_name}</span>
            <p className={styles.cellMuted}>{row.customer_phone}</p>
          </div>
        ),
      },
      {
        key: 'car_registration',
        header: 'Registration',
        render: (_v, row) => (
          <div>
            <span>{row.car_registration}</span>
            <p className={styles.cellMuted}>{row.car_make_model}</p>
          </div>
        ),
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        render: (_v, row) => formatMoney(row.amount),
      },
      {
        key: 'created_by_name',
        header: 'Created by',
        render: (_v, row) => row.created_by_name ?? '—',
      },
      {
        key: 'created_at',
        header: 'Date',
        render: (_v, row) => formatDate(row.created_at),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => (
          <StatusBadge
            status={row.status}
            statusMap={STATUS_MAP}
            formatLabel={(s) => STATUS_LABELS[s] ?? s}
          />
        ),
      },
    ],
    [navigate],
  );

  const rowActions = (row: ReceiptSummary): RowAction<ReceiptSummary>[] => {
    const actions: RowAction<ReceiptSummary>[] = [
      {
        label: 'View',
        icon: Eye,
        onClick: () => navigate(`/collections/receipts/${encodeURIComponent(row.enc_id)}`),
        variant: 'ghost',
      },
    ];
    if (row.status === 'pending_review') {
      actions.push({
        label: 'Approve',
        icon: CheckCircle2,
        variant: 'primary',
        disabled: (r) => isOwnReceipt(r),
        title: (r) => (isOwnReceipt(r) ? SELF_APPROVE_TOOLTIP : undefined),
        onClick: (r) => {
          if (isOwnReceipt(r)) return;
          setApproveTarget(r);
        },
      });
      actions.push({
        label: 'Reject',
        icon: XCircle,
        variant: 'danger',
        disabled: (r) => isOwnReceipt(r),
        title: (r) => (isOwnReceipt(r) ? SELF_APPROVE_TOOLTIP : undefined),
        onClick: (r) => {
          if (isOwnReceipt(r)) return;
          setRejectTarget(r);
          setRejectReason('');
        },
      });
    }
    if (row.status === 'pending_review' || row.status === 'rejected') {
      actions.push({
        label: 'Archive',
        icon: Archive,
        variant: 'ghost',
        onClick: (r) => setArchiveTarget(r),
      });
    }
    return actions;
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Receipt Approval Queue"
        subtitle="Review and approve pending receipts before they are sent to customers."
      />

      <div className={styles.kpiRow}>
        <Card>
          <div className={`${styles.tile} ${styles.tileWarning}`}>
            <span className={styles.tileLabel}>Pending review</span>
            <span className={styles.tileValue}>{kpis.pendingCount}</span>
          </div>
        </Card>
        <Card>
          <div className={`${styles.tile} ${styles.tileInfo}`}>
            <span className={styles.tileLabel}>Oldest pending (days)</span>
            <span className={styles.tileValue}>{kpis.oldestDays}</span>
          </div>
        </Card>
        <Card>
          <div className={`${styles.tile} ${styles.tilePrimary}`}>
            <span className={styles.tileLabel}>Total value pending</span>
            <span className={styles.tileValue}>{formatMoney(kpis.totalValue)}</span>
          </div>
        </Card>
        <Card>
          <div className={`${styles.tile} ${styles.tileSuccess}`}>
            <span className={styles.tileLabel}>Approved today</span>
            <span className={styles.tileValue}>{kpis.approvedToday}</span>
          </div>
        </Card>
      </div>

      <Card flush>
        <FilterBar
          search={{
            value: search,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by registration or customer name…',
            ariaLabel: 'Search approval queue',
          }}
        >
          <Select
            aria-label="Queue filter"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={[
              { value: 'pending_review', label: 'Pending Review' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
          <DatePicker
            aria-label="Date from"
            value={dateFrom}
            onChange={(e) => setParam('date_from', e.target.value)}
          />
          <DatePicker
            aria-label="Date to"
            value={dateTo}
            onChange={(e) => setParam('date_to', e.target.value)}
          />
        </FilterBar>

        <Table
          columns={columns}
          data={receipts}
          keyField={(row) => row.enc_id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <ClipboardCheck size={40} className={styles.emptyIcon} aria-hidden />
              <h3>
                {status === 'pending_review'
                  ? 'No receipts awaiting approval'
                  : 'No archived receipts'}
              </h3>
              <p>
                {status === 'pending_review'
                  ? 'New receipts created by drivers and staff will land here for review.'
                  : 'Archived receipts that were never approved will appear here.'}
              </p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Receipt approval queue"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{
                page,
                limit: pagination?.limit ?? KPI_PAGE_LIMIT,
                total,
                totalPages,
              }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <ConfirmModal
        open={approveTarget !== null}
        title="Approve this receipt?"
        message={
          approveTarget !== null
            ? `Approve receipt ${approveTarget.reference ?? approveTarget.unique_id}? The creator will be notified.`
            : ''
        }
        confirmLabel="Approve"
        cancelLabel="Cancel"
        loading={pendingActionEncId === approveTarget?.enc_id}
        onCancel={() => setApproveTarget(null)}
        onConfirm={() => {
          if (approveTarget !== null) void handleApproveConfirmed(approveTarget);
        }}
      />

      <ConfirmModal
        open={rejectTarget !== null}
        title="Reject this receipt?"
        message={
          <div>
            <p style={{ marginTop: 0 }}>
              {rejectTarget !== null
                ? `Reject receipt ${rejectTarget.reference ?? rejectTarget.unique_id}? The creator will be notified.`
                : ''}
            </p>
            <textarea
              aria-label="Rejection reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
              }}
              maxLength={2000}
              minLength={REJECT_MIN_LENGTH}
              placeholder={`At least ${REJECT_MIN_LENGTH} characters…`}
            />
          </div>
        }
        confirmLabel="Reject"
        cancelLabel="Cancel"
        variant="danger"
        loading={pendingActionEncId === rejectTarget?.enc_id}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
        onConfirm={() => {
          if (rejectTarget !== null) void handleRejectConfirmed(rejectTarget, rejectReason);
        }}
      />

      <ConfirmModal
        open={archiveTarget !== null}
        title="Archive this receipt?"
        message={
          archiveTarget !== null
            ? `Archive receipt ${archiveTarget.reference ?? archiveTarget.unique_id}? It will no longer appear in any active queue.`
            : ''
        }
        confirmLabel="Archive"
        cancelLabel="Cancel"
        variant="danger"
        loading={pendingActionEncId === archiveTarget?.enc_id}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={() => {
          if (archiveTarget !== null) handleArchiveConfirmed(archiveTarget);
        }}
      />
    </div>
  );
}
